'use client';
import React from 'react';
import Link from 'next/link';
import Button from '../ui/Button';

/*
  Unified CTA Component
  Props:
    - heading (string)
    - description (string)
    - primaryLabel (string)
    - primaryHref (string)
    - secondaryLabel? (string)
    - secondaryHref? (string)
    - variant: 'dark' | 'light' | 'subtle' (background treatment)
*/

const backgroundVariants = {
  dark: 'bg-[#123A5C] text-white shadow-[0_16px_40px_rgba(2,6,23,0.35)]',
  light: 'bg-[#F0F6FF] text-[#112140] shadow-[0_8px_24px_rgba(16,24,40,0.12)]',
  subtle: 'bg-[#E8ECF1] text-[#1D2B4F] shadow-[0_8px_24px_rgba(16,24,40,0.10)]'
};

const headingColorByVariant = {
  dark: 'text-white',
  light: 'text-[#112140]',
  subtle: 'text-[#1D2B4F]'
};

const descColorByVariant = {
  dark: 'text-white/90',
  light: 'text-[#475467]',
  subtle: 'text-[#5A6782]'
};

const UnifiedCTA = ({
  heading,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  variant = 'dark',
  overlap = true,                 // whether to pull card upward (legacy design)
  narrow = false,                 // constrain inner content width
  cardClassName = '',             // extra classes for card container
  primaryClassName = '',          // override/additional classes for primary button
  secondaryClassName = ''         // override/additional classes for secondary button
}) => {
  return (
    <section className="w-full pt-[24px] sm:pt-[28px] md:pt-[32px] pb-[80px] sm:pb-[100px]">
      <div className="w-full max-w-[1248px] mx-auto px-4 sm:px-6 lg:px-0">
        <div
          className={`rounded-[24px] p-[40px] sm:p-[60px] md:p-[80px] text-center ${overlap ? '-mt-[126px] sm:-mt-[142px] md:-mt-[178px]' : 'mt-0'} relative z-10 ${backgroundVariants[variant]} ${cardClassName}`}
        >
          <h2 className={`font-bold leading-tight text-[32px] sm:text-[40px] md:text-[43px] mb-[16px] ${headingColorByVariant[variant]}`}>{heading}</h2>
          {description && (
            <p className={`text-[16px] sm:text-[18px] font-normal leading-relaxed ${narrow ? 'max-w-[760px]' : 'max-w-3xl'} mx-auto mb-[32px] ${descColorByVariant[variant]}`}>
              {description}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {primaryHref && (
              <Link href={primaryHref}>
                <Button
                  variant={variant === 'dark' ? 'secondary' : 'primary'}
                  className={`${variant === 'dark'
                      ? 'bg-white hover:bg-white/90 text-[#123A5C] rounded-[30px] px-[32px] py-[16px] text-[16px] sm:text-[18px] font-semibold transition-all duration-300 shadow-lg'
                      : 'rounded-[30px] px-[32px] py-[16px] text-[16px] sm:text-[18px] font-semibold transition-all duration-300 shadow-lg'} ${primaryClassName}`}
                >
                  {primaryLabel}
                </Button>
              </Link>
            )}
            {secondaryHref && secondaryLabel && (
              <Link href={secondaryHref}>
                <Button
                  variant={variant === 'dark' ? 'ghost' : 'outline'}
                  className={`${variant === 'dark'
                      ? 'text-white border border-white/85 rounded-[30px] px-[32px] py-[16px] text-[16px] sm:text-[18px] font-semibold hover:bg-white/10'
                      : 'rounded-[30px] px-[32px] py-[16px] text-[16px] sm:text-[18px] font-semibold'} ${secondaryClassName}`}
                >
                  {secondaryLabel}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnifiedCTA;
